import { RaffleStatus } from '@/generated/graphql';
import { FanRaffleScreen } from './FanRaffleScreen';
import type { FanRafflePhase } from './fanChrome';
import { DEFAULT_RAFFLE_CLAIM_COPY } from '../drawing/ticketMotion';
import { disposalTypeForSubmissionId } from '../drawing/disposal';
import type { RaffleFanPreviewState } from './raffleFanPreviewStates';

const PREVIEW_SERIAL = '550128';
const PREVIEW_ENTRY = 7;

function submissionIdForDisposal(
  wanted: ReturnType<typeof disposalTypeForSubmissionId>,
): string {
  for (let index = 0; index < 4000; index += 1) {
    const id = `preview_sub_${index}`;
    if (disposalTypeForSubmissionId(id) === wanted) {
      return id;
    }
  }
  return 'preview_sub_0';
}

const FLAMES_SUBMISSION_ID = submissionIdForDisposal('flames');
const SAFE_SUBMISSION_ID = 'preview_sub_safe';

export function RaffleFanPreview({ state }: { state: RaffleFanPreviewState }) {
  const ticket = {
    id:
      state === 'drawing-eliminated'
        ? FLAMES_SUBMISSION_ID
        : SAFE_SUBMISSION_ID,
    signatureImageUrl: null as string | null,
  };

  const shared = {
    engagementId: 'preview',
    serial: PREVIEW_SERIAL,
    entryNumber: PREVIEW_ENTRY,
    claimCopy: DEFAULT_RAFFLE_CLAIM_COPY,
    isProduction:
      state === 'drawing-safe' ||
      state === 'drawing-eliminated' ||
      state.startsWith('completed'),
    skipTicketIntro: true,
    logoutSlot: (
      <button
        type="button"
        className="h-12 w-full max-w-[300px] self-center rounded-full bg-[#f0f0f0] text-[16px] font-bold text-[#222]"
      >
        Logout
      </button>
    ),
  };

  const byState: Record<
    RaffleFanPreviewState,
    {
      status: RaffleStatus;
      submission: typeof ticket | null;
      stillIn: boolean;
      phase: FanRafflePhase;
      isWinner: boolean;
      isLoser: boolean;
      showEntryForm: boolean;
      freezeDisposal?: boolean;
    }
  > = {
    entry: {
      status: RaffleStatus.Open,
      submission: null,
      stillIn: true,
      phase: 'idle',
      isWinner: false,
      isLoser: false,
      showEntryForm: true,
    },
    submitted: {
      status: RaffleStatus.Open,
      submission: ticket,
      stillIn: true,
      phase: 'idle',
      isWinner: false,
      isLoser: false,
      showEntryForm: false,
    },
    'ready-to-draw': {
      status: RaffleStatus.ReadyToDraw,
      submission: ticket,
      stillIn: true,
      phase: 'idle',
      isWinner: false,
      isLoser: false,
      showEntryForm: false,
    },
    'drawing-safe': {
      status: RaffleStatus.Drawing,
      submission: ticket,
      stillIn: true,
      phase: 'idle',
      isWinner: false,
      isLoser: false,
      showEntryForm: false,
    },
    'drawing-eliminated': {
      status: RaffleStatus.Drawing,
      submission: ticket,
      stillIn: false,
      phase: 'disposing',
      isWinner: false,
      isLoser: false,
      showEntryForm: false,
      freezeDisposal: true,
    },
    'completed-winner': {
      status: RaffleStatus.Completed,
      submission: ticket,
      stillIn: true,
      phase: 'idle',
      isWinner: true,
      isLoser: false,
      showEntryForm: false,
    },
    'completed-loser': {
      status: RaffleStatus.Completed,
      submission: ticket,
      stillIn: false,
      phase: 'idle',
      isWinner: false,
      isLoser: true,
      showEntryForm: false,
    },
  };

  const props = byState[state];

  return (
    <div
      data-name="RAFFLE_FAN_PREVIEW"
      data-state={state}
      className="flex min-h-screen justify-center bg-black"
    >
      <div className="mx-auto h-[844px] w-full max-w-[390px] overflow-hidden bg-black">
        <FanRaffleScreen
          {...shared}
          status={props.status}
          submission={props.submission}
          stillIn={props.stillIn}
          phase={props.phase}
          isWinner={props.isWinner}
          isLoser={props.isLoser}
          showEntryForm={props.showEntryForm}
          freezeDisposal={props.freezeDisposal}
        />
      </div>
    </div>
  );
}
